import { Badge, Button, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAccesAssistant,
  useEmettreAccesAssistant,
  useRevoquerAccesAssistant,
  type CorpsJeton,
  type OutilAssistant,
  type JetonPresente,
} from '../../api/assistantMcp.js';
import { estApiError } from '../../api/errors.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { galerieALaction } from '../../auth/sujet.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import styles from './AssistantMcpPage.module.css';

export function AssistantMcpPage(): JSX.Element {
  const { compte, galerieSlug, galerie } = useShellContext();
  // Toute transition d'identité, de galerie ou de droits détruit le secret local.
  return (
    <ContenuAssistantMcp
      key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}:${(galerie.modules ?? []).join(',')}`}
    />
  );
}
function ContenuAssistantMcp(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const jetons = useAccesAssistant(galerieSlug);
  const emettre = useEmettreAccesAssistant(galerieSlug);
  const revoquer = useRevoquerAccesAssistant(galerieSlug);
  const modifiable = galerieALaction(galerie, 'settings:write');
  const [creation, setCreation] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [aRevoquer, setARevoquer] = useState<JetonPresente | null>(null);
  const [elevation, setElevation] = useState<null | (() => void)>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const date = (valeur: string | null) =>
    valeur
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(valeur))
      : t('assistantMcp.jetons.jamais');
  const scope = (valeur: string) => {
    const [action, ressource] = valeur.split(':');
    return `${t(`assistantMcp.scopes.${ressource}`)} — ${t(`assistantMcp.actions.${action}`)}`;
  };
  const geste = (executer: () => Promise<unknown>) => {
    setErreur(null);
    void executer().catch((e: unknown) => {
      if (estPorteeObsolete(e)) return;
      if (estErreurStepUpRequis(e)) setElevation(() => () => geste(executer));
      else setErreur(estApiError(e) ? e.message : t('assistantMcp.erreurReseau'));
    });
  };
  return (
    <div className={styles.page} data-ui="assistant-mcp">
      <header>
        <h1 className={styles.titre} data-ui="page-heading">
          {t('assistantMcp.titre')}
        </h1>
        <p className={styles.sousTitre}>{t('assistantMcp.sousTitre')}</p>
        <p>{t('assistantMcp.limites')}</p>
      </header>
      <section aria-labelledby="titre-jetons" className={styles.section} data-ui="section">
        <header className={styles.entete}>
          <h2 id="titre-jetons">{t('assistantMcp.jetons.titre')}</h2>
          {modifiable && (
            <Button
              disabled={!jetons.data}
              onClick={() => {
                setErreur(null);
                setCreation(true);
              }}
            >
              {t('assistantMcp.jetons.creer')}
            </Button>
          )}
        </header>
        {erreur && !creation && aRevoquer === null && (
          <p role="alert" className={styles.erreur}>
            {erreur}
          </p>
        )}
        {jetons.isPending && <p>{t('commun:chargement')}</p>}
        {jetons.isError && (
          <p role="alert">
            {estApiError(jetons.error) ? jetons.error.message : t('assistantMcp.erreurReseau')}
          </p>
        )}
        {jetons.isSuccess && jetons.data.jetons.length === 0 && (
          <p>{t('assistantMcp.jetons.vide')}</p>
        )}
        {jetons.isSuccess && jetons.data.jetons.length > 0 && (
          <table className={styles.table} data-ui="data-table">
            <caption className={styles.masque}>{t('assistantMcp.jetons.titre')}</caption>
            <thead>
              <tr>
                {['nom', 'acces', 'cree', 'usage', 'expire', 'etat', 'actions'].map((c) => (
                  <th key={c} scope="col">
                    {t(`assistantMcp.jetons.colonnes.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jetons.data.jetons.map((j) => (
                <tr key={j.id}>
                  <th scope="row">
                    <strong>{j.nom}</strong>
                    <code className={styles.prefixe}>{j.prefixe}…</code>
                  </th>
                  <td data-label={t('assistantMcp.jetons.colonnes.acces')}>
                    <p>
                      {t(
                        `assistantMcp.profils.${profilDesScopes(j.scopes, jetons.data.scopesAccordables)}`,
                      )}
                    </p>
                    <div className={styles.scopes}>
                      {j.scopes.map((s) => (
                        <Badge key={s} tone="neutral">
                          {scope(s)}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td data-label={t('assistantMcp.jetons.colonnes.cree')}>
                    {date(j.creeLe)}
                    <small>{j.creePar}</small>
                  </td>
                  <td data-label={t('assistantMcp.jetons.colonnes.usage')}>
                    {date(j.dernierUsageLe)}
                  </td>
                  <td data-label={t('assistantMcp.jetons.colonnes.expire')}>{date(j.expireLe)}</td>
                  <td data-label={t('assistantMcp.jetons.colonnes.etat')}>
                    <Badge tone={j.etat === 'ACTIF' ? 'available' : 'neutral'}>
                      {t(`assistantMcp.jetons.etats.${j.etat}`)}
                    </Badge>
                  </td>
                  <td>
                    {modifiable && j.etat !== 'REVOQUE' && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setErreur(null);
                          setARevoquer(j);
                        }}
                      >
                        {t('assistantMcp.jetons.revoquer')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {creation && (
        <DialogueCreationAcces
          scopes={jetons.data?.scopesAccordables ?? []}
          durees={jetons.data?.durees ?? []}
          outils={jetons.data?.outils ?? []}
          enCours={emettre.isPending}
          erreur={erreur}
          onFermer={() => setCreation(false)}
          onValider={(corps) =>
            geste(async () => {
              const r = await emettre.mutateAsync(corps);
              setCreation(false);
              setSecret(r.secret);
            })
          }
        />
      )}
      {secret !== null && (
        <DialogueSecret
          secret={secret}
          urlMcp={jetons.data?.urlMcp ?? ''}
          onFermer={() => setSecret(null)}
        />
      )}
      <Dialog
        open={aRevoquer !== null}
        onOpenChange={(o) => {
          if (!o) setARevoquer(null);
        }}
        title={t('assistantMcp.revocation.titre')}
        description={t('assistantMcp.revocation.texte')}
        closeLabel={t('commun:fermer')}
        footer={
          <Button
            variant="danger"
            disabled={revoquer.isPending}
            onClick={() => {
              if (aRevoquer)
                geste(async () => {
                  await revoquer.mutateAsync(aRevoquer.id);
                  setARevoquer(null);
                });
            }}
          >
            {t('assistantMcp.jetons.revoquer')}
          </Button>
        }
      >
        <p>{aRevoquer?.nom}</p>
        {erreur && (
          <p role="alert" className={styles.erreur}>
            {erreur}
          </p>
        )}
      </Dialog>
      <StepUpDialog
        open={elevation !== null}
        onOpenChange={(o) => {
          if (!o) setElevation(null);
        }}
        onVerifie={() => {
          const rejouer = elevation;
          setElevation(null);
          rejouer?.();
        }}
        description={t('assistantMcp.elevation')}
      />
    </div>
  );
}
function profilDesScopes(scopes: readonly string[], accordables: readonly string[]): string {
  const lectures = accordables.filter((s) => s.startsWith('read:'));
  if (scopes.length === lectures.length && lectures.every((s) => scopes.includes(s)))
    return 'consultation';
  if (scopes.length === accordables.length && accordables.every((s) => scopes.includes(s)))
    return 'saisie';
  return 'detail';
}

function DialogueCreationAcces({
  scopes,
  durees,
  outils,
  enCours,
  erreur,
  onFermer,
  onValider,
}: {
  scopes: readonly string[];
  durees: readonly number[];
  outils: readonly OutilAssistant[];
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onValider: (c: CorpsJeton) => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [nom, setNom] = useState('');
  const [duree, setDuree] = useState(String(durees.includes(90) ? 90 : durees[0]));
  const [profil, setProfil] = useState('consultation');
  const [choisis, setChoisis] = useState<string[]>(scopes.filter((s) => s.startsWith('read:')));
  const groupes = [...new Set(scopes.map((s) => s.split(':')[1] ?? ''))];
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !enCours) onFermer();
      }}
      title={t('assistantMcp.creation.titre')}
      closeLabel={t('commun:fermer')}
      description={t('assistantMcp.creation.acces')}
    >
      {erreur && (
        <p role="alert" className={styles.erreur}>
          {erreur}
        </p>
      )}
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          onValider({
            nom,
            scopes: choisis,
            expireDansJours: Number(duree) as CorpsJeton['expireDansJours'],
          });
        }}
      >
        <Field label={t('assistantMcp.creation.nom')} htmlFor="nom-jeton">
          <Input
            id="nom-jeton"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            minLength={2}
            maxLength={120}
            required
            autoComplete="off"
          />
        </Field>
        <Field label={t('assistantMcp.creation.duree')} htmlFor="duree-jeton">
          <Select
            id="duree-jeton"
            value={duree}
            onValueChange={setDuree}
            options={durees.map((n) => ({
              value: String(n),
              label: t('assistantMcp.creation.jours', { n }),
            }))}
          />
        </Field>
        <Field label={t('assistantMcp.creation.profil')} htmlFor="profil-assistant">
          <Select
            id="profil-assistant"
            value={profil}
            onValueChange={(valeur) => {
              setProfil(valeur);
              if (valeur === 'consultation')
                setChoisis(scopes.filter((s) => s.startsWith('read:')));
              if (valeur === 'saisie') setChoisis([...scopes]);
            }}
            options={['consultation', 'saisie', 'detail'].map((value) => ({
              value,
              label: t(`assistantMcp.profils.${value}`),
            }))}
          />
        </Field>
        {profil === 'detail' && (
          <div className={styles.groupes}>
            {groupes.map((r) => (
              <fieldset key={r}>
                <legend>{t(`assistantMcp.scopes.${r}`)}</legend>
                {scopes
                  .filter((s) => s.endsWith(`:${r}`))
                  .map((s) => (
                    <Checkbox
                      key={s}
                      id={`scope-${s}`}
                      label={`${t(`assistantMcp.scopes.${r}`)} — ${t(`assistantMcp.actions.${s.split(':')[0]}`)}`}
                      checked={choisis.includes(s)}
                      onCheckedChange={(c) =>
                        setChoisis((v) => (c === true ? [...v, s] : v.filter((x) => x !== s)))
                      }
                    />
                  ))}
              </fieldset>
            ))}
          </div>
        )}
        <section aria-labelledby="outils-assistant">
          <h3 id="outils-assistant">{t('assistantMcp.creation.outils')}</h3>
          <ul>
            {outils
              .filter((o) => o.scopes.every((s) => choisis.includes(s)))
              .map((o) => (
                <li key={o.nom}>
                  {o.titre} <code>({o.nom})</code>
                </li>
              ))}
          </ul>
        </section>
        <Button type="submit" disabled={enCours || nom.trim().length < 2 || choisis.length === 0}>
          {t('assistantMcp.creation.valider')}
        </Button>
      </form>
    </Dialog>
  );
}
function DialogueSecret({
  secret,
  urlMcp,
  onFermer,
}: {
  secret: string;
  urlMcp: string;
  onFermer: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [copie, setCopie] = useState('');
  const configurations = [
    {
      cle: 'claudeCode',
      valeur: `claude mcp add --transport http artosera ${urlMcp} --header "Authorization: Bearer ${secret}"`,
    },
    {
      cle: 'json',
      valeur: JSON.stringify(
        {
          mcpServers: { artosera: { url: urlMcp, headers: { Authorization: `Bearer ${secret}` } } },
        },
        null,
        2,
      ),
    },
    {
      cle: 'distant',
      valeur: `npx mcp-remote ${urlMcp} --header "Authorization: Bearer ${secret}"`,
    },
  ];
  const copier = (valeur: string) => {
    if (!navigator.clipboard) {
      setCopie(t('assistantMcp.secret.echecCopie'));
      return;
    }
    void navigator.clipboard
      .writeText(valeur)
      .then(() => setCopie(t('assistantMcp.secret.copie')))
      .catch(() => setCopie(t('assistantMcp.secret.echecCopie')));
  };
  return (
    <Dialog
      open
      onOpenChange={() => undefined}
      title={t('assistantMcp.secret.titre')}
      description={t('assistantMcp.secret.avertissement')}
      closeLabel={t('commun:fermer')}
      footer={<Button onClick={onFermer}>{t('assistantMcp.secret.compris')}</Button>}
    >
      <div className={styles.formulaire}>
        <Input
          aria-label={t('assistantMcp.secret.titre')}
          value={secret}
          readOnly
          autoComplete="off"
          spellCheck={false}
          onFocus={(e) => e.target.select()}
        />
        <Button variant="secondary" onClick={() => copier(secret)}>
          {t('assistantMcp.secret.copier')}
        </Button>
        <Field label={t('assistantMcp.secret.url')} htmlFor="url-mcp">
          <Input id="url-mcp" value={urlMcp} readOnly onFocus={(e) => e.target.select()} />
        </Field>
        {configurations.map(({ cle, valeur }) => (
          <section key={cle} aria-labelledby={`configuration-${cle}`}>
            <h3 id={`configuration-${cle}`}>{t(`assistantMcp.secret.${cle}`)}</h3>
            <pre className={styles.configuration}>
              <code>{valeur}</code>
            </pre>
            <Button
              variant="secondary"
              onClick={() => copier(valeur)}
              aria-label={t('assistantMcp.secret.copierConfiguration', {
                nom: t(`assistantMcp.secret.${cle}`),
              })}
            >
              {t('assistantMcp.secret.copier')}
            </Button>
          </section>
        ))}
        <p role="status">{copie}</p>
      </div>
    </Dialog>
  );
}
